import { useState, useEffect, useMemo, useCallback } from "react";
import { getNivelAcessoFromUser } from "../js/auth.js";

/**
 * Mapeamento de permissões por acesso_autorizacao:
 * 0 = Empresa
 * 1 = Estudante
 * 2 = SEOPE
 * 3 = SECEX
 * 4 = INFOR
 */
const PERMISSOES = {
  0: {
    label: "Empresa",
    podeEditarEmpresa: true,
    podeEditarEstudante: false,
    podeEditarTudo: false,
    podeAprovar: false,
    podeReprovar: false,
    podeSubmeter: false,
    podeFinalizar: false,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: false,
  },
  1: {
    label: "Estudante",
    podeEditarEmpresa: false,
    podeEditarEstudante: true,
    podeAprovar: false,
    podeReprovar: false,
    podeSubmeter: true,
    podeFinalizar: false,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: false,
  },
  2: {
    label: "SEOPE",
    podeEditarEmpresa: true,
    podeEditarEstudante: true,
    podeEditarTudo: true,
    podeAprovar: true,
    podeReprovar: true,
    podeSubmeter: false,
    podeFinalizar: true,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: true,
  },
  3: {
    label: "SECEX",
    podeEditarEmpresa: true,
    podeEditarEstudante: true,
    podeEditarTudo: true,
    podeAprovar: true,
    podeReprovar: true,
    podeSubmeter: false,
    podeFinalizar: true,
    podeVerHistorico: true,
    podeAnexar: false,
    podeVerObservacoesCiee: true,
  },
  4: {
    label: "INFOR",
    podeEditarEmpresa: true,
    podeEditarEstudante: true,
    podeEditarTudo: true,
    podeAprovar: true,
    podeReprovar: true,
    podeSubmeter: true,
    podeFinalizar: true,
    podeVerHistorico: true,
    podeAnexar: true,
    podeVerObservacoesCiee: true,
  },
};

const PERMISSAO_DEFAULT = PERMISSOES[0];

export function usePermissoes() {
  const [user, setUser] = useState(() => {
    try {
      if (typeof window === "undefined") return null;
      const userStr =
        localStorage.getItem("user") || localStorage.getItem("usuario");
      if (userStr && userStr !== "undefined") {
        return JSON.parse(userStr);
      }
    } catch (err) {
      console.error("Erro ao ler user do localStorage:", err);
    }
    return null;
  });

  useEffect(() => {
    const handleStorage = () => {
      try {
        const userStr =
          localStorage.getItem("user") || localStorage.getItem("usuario");
        setUser(userStr && userStr !== "undefined" ? JSON.parse(userStr) : null);
      } catch (_) {}
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const acesso = useMemo(() => {
    return user ? getNivelAcessoFromUser(user) : 0;
  }, [user]);

  const permissao = useMemo(() => {
    return PERMISSOES[acesso] || PERMISSAO_DEFAULT;
  }, [acesso]);

  const podeEditarCampo = useCallback(
    (secao) => {
      if (permissao.podeEditarTudo) return true;

      if (
        secao === "empresa" ||
        secao === "supervisor" ||
        secao === "estagio" ||
        secao === "horarios" ||
        secao === "comprovante" ||
        secao === "observacoes"
      ) {
        return permissao.podeEditarEmpresa;
      }

      if (secao === "estudante" || secao === "escolares" || secao === "anexos") {
        return permissao.podeEditarEstudante;
      }

      return false;
    },
    [permissao],
  );

  return {
    user,
    permissao,
    acessoAutorizacao: acesso,
    tipoLabel: permissao.label,
    podeEditarCampo,
    isEmpresa: permissao.label === "Empresa",
    isEstudante: permissao.label === "Estudante",
    isCieemg: ["SEOPE", "SECEX", "INFOR"].includes(permissao.label),
  };
}

export default usePermissoes;
